import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Loader2, Car, Users, Clock, Check, Leaf, ShieldCheck } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1616455436127-553454f32623?crop=entropy&cs=srgb&fm=jpg&q=85&w=940";

export default function TransferResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { formatPrice } = useApp();
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evOnly, setEvOnly] = useState(false);

  const search = {
    destination: params.get("destination") || "AYT",
    airportId: params.get("airportId") || "",
    locationId: params.get("locationId") || "",
    direction: params.get("direction") || "arrival",
    date: params.get("date") || new Date(Date.now() + 21 * 864e5).toISOString().slice(0, 10),
    time: params.get("time") || "10:00",
    adults: +(params.get("adults") || 2),
  };

  useEffect(() => {
    setLoading(true);
    api.post("/transfers/search", search).then((r) => setTransfers(r.data.transfers)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [params]);

  const book = (t) => navigate("/checkout", { state: { item: {
    productType: "transfer",
    source: t.src,
    title: `${t.vehicle} · ${t.category || t.type}`,
    subtitle: `${t.fromName} → ${t.toName} · ${search.date} ${search.time}`,
    image: t.image || FALLBACK,
    priceEur: t.priceEur,
    rateKey: t.rateKey,
    meta: { ...(t.meta || {}), date: search.date, time: search.time, direction: search.direction },
  } } });

  const isEv = (t) => (t.id || "").includes("ev");
  const poweredByFleet = transfers.some((t) => t.src === "vayatransfer");
  const hasEv = transfers.some(isEv);
  const shown = evOnly ? transfers.filter(isEv) : transfers;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="pt-24 max-w-5xl mx-auto px-5 lg:px-8">
        <span className="label-caps">Airport transfers</span>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-[#0B2038] mt-1">
          {loading ? "Searching transfers…" : `${shown.length} vehicles available`}
        </h1>

        {!loading && (poweredByFleet || hasEv) && (
          <div className="flex flex-wrap items-center gap-3 mt-3 mb-6" data-testid="transfer-toolbar">
            {poweredByFleet && (
              <span data-testid="powered-by-fleet" className="inline-flex items-center gap-2 rounded-full bg-[#0B2038] text-white text-xs font-bold px-3.5 py-2">
                <ShieldCheck className="w-4 h-4 text-[#E3BE4F]" /> Powered by our own transfer fleet
              </span>
            )}
            {hasEv && (
              <button data-testid="ev-filter-toggle" onClick={() => setEvOnly((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-full text-xs font-bold px-3.5 py-2 border transition-colors ${evOnly ? "bg-[#009E9E] text-white border-[#009E9E]" : "bg-white text-[#0B2038] border-[#E2E8F0] hover:border-[#009E9E]"}`}>
                <Leaf className="w-4 h-4" /> {evOnly ? "Showing electric only" : "Green rides (electric)"}
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center py-24 text-[#475569]"><Loader2 className="w-8 h-8 animate-spin mb-3 text-[#009E9E]" />Loading transfers…</div>
        ) : shown.length === 0 ? (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 text-center text-[#475569]" data-testid="transfer-empty">No transfers available.</div>
        ) : (
          <div className="space-y-4">
            {shown.map((t) => (
              <div key={t.id} data-testid={`transfer-card-${t.id}`} className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden grid md:grid-cols-12 transition-transform duration-300 hover:-translate-y-1">
                <div className="md:col-span-3 h-40 md:h-auto bg-[#E2E8F0]">
                  <img src={t.image || FALLBACK} alt={t.vehicle} className="w-full h-full object-cover" onError={(e) => { e.target.src = FALLBACK; }} />
                </div>
                <div className="md:col-span-6 p-5">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="inline-block text-xs font-semibold text-[#0B2038] bg-[#F8FAFC] border border-[#E2E8F0] rounded-full px-3 py-1">{t.transferType || t.type}</span>
                    {isEv(t) && <span data-testid={`ev-badge-${t.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#047857] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2.5 py-1"><Leaf className="w-3 h-3" />Electric</span>}
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#0B2038]">{t.vehicle}</h3>
                  <p className="text-[#475569] text-sm mt-1">{t.fromName} → {t.toName}</p>
                  <div className="flex gap-4 mt-3 text-xs text-[#475569]">
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />up to {t.maxPax}</span>
                    <span className="flex items-center gap-1"><Check className="w-3.5 h-3.5 text-[#009E9E]" />Meet &amp; greet</span>
                  </div>
                </div>
                <div className="md:col-span-3 p-5 border-t md:border-t-0 md:border-l border-[#E2E8F0] flex flex-col justify-between items-start md:items-end">
                  <div className="md:text-right"><p className="text-xs text-[#475569]">total</p><p className="font-display text-2xl font-bold text-[#0B2038]">{formatPrice(t.priceEur)}</p></div>
                  <Button data-testid={`transfer-book-${t.id}`} onClick={() => book(t)} className="mt-3 w-full md:w-auto rounded-full bg-[#009E9E] hover:bg-[#007A7A] text-white font-semibold">Book</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
